import type { AircraftSpec, DraftMission, FlightLeg, Mission } from "./types";

export function routeLabel(m: Pick<DraftMission, "originIcao" | "destIcao" | "legs">): string {
  if (m.legs && m.legs.length > 1) {
    const first = m.legs[0];
    if (!first) return `${m.originIcao}–${m.destIcao}`;
    return [first.originIcao, ...m.legs.map((l) => l.destIcao)].join("–");
  }
  return `${m.originIcao}–${m.destIcao}`;
}

/** The tail on the fleet book with this id. An empty book does not invent a catalog type. */
export function specFor(id: DraftMission["aircraftId"], fleet: AircraftSpec[] = []): AircraftSpec | undefined {
  return fleet.find((a) => a.id === id);
}

export function ensureLegs(m: DraftMission | Mission): FlightLeg[] {
  if (m.legs && m.legs.length > 0) return m.legs;
  return [
    {
      seq: 1,
      originIcao: m.originIcao,
      destIcao: m.destIcao,
      originName: m.originName,
      destName: m.destName,
      originCity: m.originCity,
      destCity: m.destCity,
      distanceNm: m.distanceNm,
      eteMin: m.eteMin,
      fuelGal: m.fuelGal,
      fuelLbs: m.fuelLbs,
      trackTrue: m.trackTrue,
      departureIso: m.departureIso,
      originMetar: m.originMetar,
      destMetar: m.destMetar,
      originRunwayFt: m.originRunwayFt,
      destRunwayFt: m.destRunwayFt,
      paxCount: m.paxCount,
      paxNames: m.paxNames,
      notes: "",
    },
  ];
}

export function withLegs<T extends DraftMission | Mission>(m: T): T {
  const legs = ensureLegs(m);
  const last = legs[legs.length - 1] ?? legs[0];
  const first = legs[0];
  if (!first || !last) return { ...m, legs };
  return {
    ...m,
    legs,
    originIcao: first.originIcao,
    destIcao: last.destIcao,
    originName: first.originName,
    destName: last.destName,
    originCity: first.originCity,
    destCity: last.destCity,
    distanceNm: legs.reduce((s, l) => s + l.distanceNm, 0),
    eteMin: legs.reduce((s, l) => s + l.eteMin, 0),
    fuelGal: legs.reduce((s, l) => s + l.fuelGal, 0),
    fuelLbs: legs.reduce((s, l) => s + l.fuelLbs, 0),
    trackTrue: first.trackTrue,
    paxCount: first.paxCount,
    paxNames: first.paxNames,
    departureIso: first.departureIso,
    originMetar: first.originMetar,
    destMetar: last.destMetar,
    originRunwayFt: first.originRunwayFt,
    destRunwayFt: last.destRunwayFt,
  };
}
